/**
 * Shared DnD helpers for List editor and Checklist page
 * (CHECKLIST_VIEW_ORGANIZE_PLAN Decisions #13–15).
 *
 * ## Container id conventions
 *
 * List editor item containers:
 *   - `"ungrouped"` — root items not in a subgroup
 *   - `"subgroup:{subgroupId}"` — items inside a named subgroup
 *
 * List editor subgroup-block container (separate SortableContext):
 *   - use each subgroup's `id` as the sortable id
 *
 * Checklist page item containers (Session 5):
 *   - `"s{sectionIdx}:ungrouped"`
 *   - `"s{sectionIdx}:sg:{subgroupId}"`
 *
 * Checklist section blocks: use `"section:{sectionIdx}"` (or stable section keys).
 *
 * ## Usage sketch
 *
 * ```tsx
 * const sensors = useDndSensors();
 * <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
 *   <SortableContext items={ids} strategy={verticalListSortingStrategy}>
 *     {ids.map((id) => (
 *       <SortableRow key={id} id={id}>
 *         {({ dragHandleProps, isDragging }) => (
 *           <li className={isDragging ? "opacity-50" : undefined}>
 *             <DragHandle {...dragHandleProps} />
 *             ...
 *           </li>
 *         )}
 *       </SortableRow>
 *     ))}
 *   </SortableContext>
 * </DndContext>
 * ```
 *
 * Empty containers need `useDroppable({ id: containerId })` so items can be dropped into them.
 * Prefer `onReorder`-style handlers that resolve active/over to container + index, then
 * mutate local state and persist via PUT.
 */

import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DndContextProps,
  type DragEndEvent,
  type UniqueIdentifier,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

export {
  DndContext,
  SortableContext,
  closestCenter,
  arrayMove,
  verticalListSortingStrategy,
};
export type { DragEndEvent, UniqueIdentifier };

/** PointerSensor with distance: 8 so touch scrolling still works (D-24). */
export function useDndSensors() {
  return useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );
}

export type DragHandleProps = {
  attributes: ReturnType<typeof useSortable>["attributes"];
  listeners: ReturnType<typeof useSortable>["listeners"];
};

type DragHandleButtonProps = DragHandleProps & {
  disabled?: boolean;
  label?: string;
  className?: string;
};

/** Grip handle — spread `attributes` + `listeners` from useSortable / SortableRow. */
export function DragHandle({
  attributes,
  listeners,
  disabled = false,
  label = "Drag to reorder",
  className = "",
}: DragHandleButtonProps) {
  return (
    <button
      type="button"
      className={
        "inline-flex size-8 shrink-0 touch-none items-center justify-center rounded-lg p-0 text-muted-sm hover:bg-slate-100 hover:text-ink " +
        "disabled:cursor-not-allowed disabled:opacity-40 " +
        className
      }
      aria-label={label}
      disabled={disabled}
      {...attributes}
      {...listeners}
    >
      <GripVertical className="h-[18px] w-[18px]" aria-hidden />
    </button>
  );
}

type SortableRowRenderArgs = {
  dragHandleProps: DragHandleProps;
  isDragging: boolean;
  setNodeRef: (node: HTMLElement | null) => void;
  style: CSSProperties;
};

type SortableRowProps = {
  id: UniqueIdentifier;
  disabled?: boolean;
  /** Passed to useSortable — e.g. `{ type: "item" | "subgroup", containerId? }`. */
  data?: Record<string, unknown>;
  children: (args: SortableRowRenderArgs) => ReactNode;
};

/** Wraps useSortable; render-prop supplies handle props + dragging styles. */
export function SortableRow({
  id,
  disabled = false,
  data,
  children,
}: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled, data });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : undefined,
    position: "relative",
  };

  return (
    <>
      {children({
        dragHandleProps: { attributes, listeners },
        isDragging,
        setNodeRef,
        style,
      })}
    </>
  );
}

/** List-editor item container id helpers. */
export function ungroupedContainerId(): string {
  return "ungrouped";
}

export function subgroupContainerId(subgroupId: string): string {
  return `subgroup:${subgroupId}`;
}

export function parseSubgroupContainerId(containerId: string): string | null {
  if (!containerId.startsWith("subgroup:")) return null;
  return containerId.slice("subgroup:".length) || null;
}

/** Checklist item container id helpers (for Session 5). */
export function checklistUngroupedContainerId(sectionIdx: number): string {
  return `s${sectionIdx}:ungrouped`;
}

export function checklistSubgroupContainerId(
  sectionIdx: number,
  subgroupId: string
): string {
  return `s${sectionIdx}:sg:${subgroupId}`;
}

export function parseChecklistContainerId(
  containerId: string
): { sectionIdx: number; subgroupId: string | null } | null {
  const ungrouped = /^s(\d+):ungrouped$/.exec(containerId);
  if (ungrouped) {
    return { sectionIdx: Number(ungrouped[1]), subgroupId: null };
  }
  const sg = /^s(\d+):sg:(.+)$/.exec(containerId);
  if (sg) {
    return { sectionIdx: Number(sg[1]), subgroupId: sg[2]! };
  }
  return null;
}

export function sectionSortableId(sectionIdx: number): string {
  return `section:${sectionIdx}`;
}

export function parseSectionSortableId(id: string): number | null {
  const m = /^section:(\d+)$/.exec(id);
  return m ? Number(m[1]) : null;
}

/** Stable sortable id for a subgroup block header (not the item container). */
export function subgroupBlockSortableId(
  sectionIdx: number,
  subgroupId: string
): string {
  return `sgblock:${sectionIdx}:${subgroupId}`;
}

export function parseSubgroupBlockSortableId(
  id: string
): { sectionIdx: number; subgroupId: string } | null {
  const m = /^sgblock:(\d+):(.+)$/.exec(id);
  if (!m) return null;
  return { sectionIdx: Number(m[1]), subgroupId: m[2]! };
}

type SortableListProps = {
  /** Sortable item ids in this container (order = visual order). */
  items: UniqueIdentifier[];
  disabled?: boolean;
  children: ReactNode;
  /** Optional class on the SortableContext wrapper div. */
  className?: string;
} & Pick<DndContextProps, never>;

/**
 * Thin SortableContext wrapper with vertical strategy.
 * Parent must provide DndContext (often one per page for cross-container moves).
 */
export function SortableList({
  items,
  disabled = false,
  children,
  className,
}: SortableListProps) {
  return (
    <SortableContext
      items={items}
      strategy={verticalListSortingStrategy}
      disabled={disabled}
    >
      <div className={className}>{children}</div>
    </SortableContext>
  );
}
